/**
 * Created by robert.lavoie on 6/20/17.
 */

import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

// Models
import { Site } from 'careerpages-base/models';

// Services
import { ReportingService, SiteService, LinkService } from '../../services';


const enum ApplySessionType {
  ThirdParty = '4',
  DirectApply = '7'
}

/**
 *
 * @class
 * JobDetailComponent
 *
 * @description
 * This component renders the job details data
 *
 */
@Component({
  templateUrl: './job-detail.component.html',
  selector: 'job-detail',
  styleUrls: ['./job-detail.component.scss'],
})
export class JobDetailComponent implements OnInit {

  public jobDetails: object;
  public wageString: string;
  public site: Site;
  public showApplyModal: boolean = false;

  // set to true when not applying via snagajob
  public isExternalApply: boolean = false;

  public showShare: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private siteService: SiteService,
    private meta: Meta,
    private title: Title,
    @Inject(PLATFORM_ID) private platformId: Object,
    private reportingService: ReportingService,
    private linkService: LinkService
  ) {}

  ngOnInit() {

    this.site = this.siteService.getSite();

    // Set the canonical url
    this.linkService.addTag({
      rel: 'canonical',
      href: this.siteService.getCurrentUrl()
    });


    this.route.data
      .subscribe((data) => {

        if (data['jobDetails']) {
          this.jobDetails = data['jobDetails'];
          const name = (this.jobDetails['brandTemplateName']) ? this.jobDetails['brandTemplateName'] : this.jobDetails['company'];

          // Create the wage string to display the wage
          if (this.jobDetails['payRate']) {
            this.wageString = this.buildWageString(this.jobDetails['payRate']);
          }

          // is exteral for ThirdParty or DirectApply applications
          this.isExternalApply = (this.jobDetails['applySessionType'] === ApplySessionType.DirectApply || this.jobDetails['applySessionType'] === ApplySessionType.ThirdParty) ? true : false;

          // tslint:disable-next-line:max-line-length
          this.title.setTitle(`${name} ${this.jobDetails['jobTitle']} Job in ${this.jobDetails['county']}, ${this.jobDetails['stateProvCode']} | ${this.site.settings.name}`);

          // set open graph data for Facebook share
          const metatags = [];

          metatags.push({
            name: 'description',
            content: `Apply for a ${name} ${this.jobDetails['jobTitle']} Job in ${this.jobDetails['county']}, ${this.jobDetails['stateProvCode']}. Apply online today and join our team!`
          });

          if (this.site.theme.logo) {
            metatags.push({ name: 'og:image', content: this.site.theme.logo.url });
          }

          this.meta.addTags(metatags);

          if (isPlatformBrowser(this.platformId)) {
            this.reportingService.pageView({
              pageName: 'cp-job-details',
              eVar17: this.jobDetails['id'],
              eVar19: this.jobDetails['billingAccountId'],
              eVar20: this.jobDetails['brandId'],
              eVar21: this.jobDetails['jobTitle'],
              eVar22: this.jobDetails['company'],
              eVar23: this.jobDetails['city'],
              eVar24: this.jobDetails['stateProvCode'],
              eVar25: this.jobDetails['postalCode'],
              eVar26: this.jobDetails['primaryIndustryId'],
              prop17: this.jobDetails['id'],
              prop19: this.jobDetails['billingAccountId'],
              prop20: this.jobDetails['brandId'],
              prop21: this.jobDetails['jobTitle'],
              prop22: this.jobDetails['company'],
              prop23: this.jobDetails['city'],
              prop24: this.jobDetails['stateProvCode'],
              prop25: this.jobDetails['postalCode'],
              prop26: this.jobDetails['primaryIndustryId'],
            });

						// Fetch the position of the job in search results
  					let jobListingPosition: number = null;
            if (this.reportingService.jobListingIndex !== null) {
              jobListingPosition = this.reportingService.jobListingIndex + 1;
              this.reportingService.clearJobPostingIndex();
            }

            this.reportingService.internalEvent({
              EventType: 'Posting Detail Impression',
              Placement: 'Job Details',
              PostingId: this.jobDetails['id'],
              BillingAccountId: this.jobDetails['billingAccountId'],
              ItemNumber: jobListingPosition,
            });
          }

        } else {

          this.title.setTitle(`404 | ${this.site.settings.name}`);

          this.meta.addTags([
                  { name: 'description', content: `Apply online today and join our team!` }
          ]);

        }

      });
  }


  /**
   * Toggles the display of the apply modal
   */
  toggleApplyModal(){
    this.showApplyModal = this.showApplyModal === true ? false : true;

    // If the modal is open, fire the apply intent event
    if (this.showApplyModal) {
      this.reportingService.trackCustomEvent('cp_applyintent');
    }
  }

  /**
   * Handles the continue button which does reporting, modal logic, and redirect
   */
  applyContinueClick() {

    if (!this.isExternalApply) {
      this.reportingService.trackCustomEvent('cp_applycontinue');
      this.toggleApplyModal();
    }else{
      this.reportingService.trackCustomEvent('cp_applycontinue_xats');
    }

    const siteUrl = encodeURIComponent(this.siteService.currentSiteUrl);
    let applyUrl = environment.APPLY_WEB_URL + `/${this.jobDetails['id']}?cp=true&ref=cp_${siteUrl}`;

    // If we have an application URL, prefer that
    if (this.jobDetails['applicationURL']) {
      applyUrl = this.jobDetails['applicationURL'];
      // This replaces the appSourceId in the applicationURL from Snagajob, to one for career pages and or please apply online
      const urlSegments = this.jobDetails['applicationURL'].split('/');
      // if the last path in url is equal 365 we need to change it to 2 for weird reasons
      if (urlSegments.pop(-1) === '365') {
        applyUrl = `${urlSegments.join('/')}/2`;
      }
    }

    // Conditional for crawlers/universal
    if (window) window.open(applyUrl);
  }

  /**
   * Builds the category string for a single job
   * @param job {any} - single row of the results we get back from the API search
   * @returns {string} - comma separated list of categories for a specific job
   */
  buildCategoryString(job: any): string {
    if (job.categories) {
      return job.categories.map(x => x).join(', ');
    }
  }

  /**
   * Given a job, build the addres string to display on the detail view
   * @param { any } job - job we are currently viewing
   */
  buildAddressString(job: any): string {
    return `${job.addressLine1} ${job.city} ${job.stateProvCode}`;
  }

  /**
   * Given a wage string form the API, attempt to parse it into a usable float or use what was provided
   * @param { string } wage - the raw wage string from the API
   */
  buildWageString(wage: string): string {
    // Parse the wage as a float and if it is NaN then return the provided string
    // also return if it parses to zero
    const parsedWage: number = parseFloat(wage);
    if (Number.isNaN(parsedWage) || parsedWage <= 0) return wage;

    // Otherwise we continue and format the provided decimal value
    return `\$${parsedWage.toFixed(2)} per hour`;
  }

}
